import { DeleteOutline, MailOutline, PersonAddAlt, Security } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Snackbar,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    AppRoleAssignment,
    CreateAzureUserModel,
    ModifyUsersInGroupsModel,
} from "src/App/api/APIClient";
import { userAzureService } from "src/App/api/Services";
import { usePanelProjectContext } from "src/App/BIMDigitalTwin/PanelProjectProvider";
import { v4 as uuid } from "uuid";
import AssociateUsersRoles from "./AssociateUsersRoles";
import CreateUser from "./CreateUser";
import InviteExternUser from "./InviteExternUser";

export interface PendingUser extends CreateAzureUserModel {
    id: string;
}

export type UserTab = "create" | "invite" | "associate";

function a11yProps(index: number) {
    return {
        id: `user-tab-${index}`,
        "aria-controls": `user-tabpanel-${index}`,
    } as const;
}

export default function UserSettingsModal({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const { t } = useTranslation("project");
    const { project } = usePanelProjectContext();

    const [userType, setUserType] = useState<UserTab>("create");
    const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isProcessingRoles, setIsProcessingRoles] = useState(false);

    const [roles, setRoles] = useState<AppRoleAssignment[]>([]);
    const [validDomain, setValidDomain] = useState<string>();

    const [associateRolesUsers, setAssociateRolesUsers] = useState<ModifyUsersInGroupsModel[]>([]);

    // Snackbar
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        severity: "success" | "error";
        message: string;
    }>({ open: false, severity: "success", message: "" });

    const openSnack = (message: string, severity: "success" | "error") =>
        setSnackbar({ open: true, message, severity });

    const tabIndex = useMemo(() => {
        switch (userType) {
            case "create":
                return 0;
            case "invite":
                return 1;
            case "associate":
                return 2;
            default:
                return 3;
        }
    }, [userType]);

    useEffect(() => {
        let mounted = true;
        userAzureService.getAvailableRoles({ projectId: project.id }).then((res) => {
            if (mounted) setRoles(res.data);
        });

        userAzureService.getValidDomain({ projectId: project.id }).then((res) => {
            if (mounted) setValidDomain(res.data);
        });

        return () => {
            mounted = false;
        };
    }, [project.id]);

    const resetAll = () => {
        setPendingUsers([]);
    };

    const handleAddUser = (input: {
        isNewUser: boolean;
        emailOrUsername: string;
        name?: string;
        roleId: string;
        allowAccess: boolean;
    }) => {
        const email = input.isNewUser ? `${input.emailOrUsername}` : input.emailOrUsername;

        const newUser: PendingUser = {
            id: uuid(),
            isNewUser: input.isNewUser,
            email,
            name: input.name ?? "",
            roleId: input.roleId,
            allowAccess: input.allowAccess,
        };

        setPendingUsers((prev) => [...prev, newUser]);
    };

    const handleRemoveUser = (id: string) => {
        setPendingUsers((prev) => prev.filter((u) => u.id !== id));
    };

    const handleProcessAll = async () => {
        if (pendingUsers.length === 0) return;
        setIsProcessing(true);

        userAzureService
            .createAzureUsers({
                projectId: project.id,
                users: pendingUsers,
            })
            .then((res) => {
                setPendingUsers([]);
                if (res.data.length > 0) {
                    const element = document.createElement("a");
                    const file = new Blob([res.data.join("\n")], { type: "text/plain" });
                    element.href = URL.createObjectURL(file);
                    element.download = "new_accounts.txt";
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                }
                openSnack(t("userController.snackbar.processSuccess"), "success");
                onOpenChange(false); // cerrar modal al éxito
            })
            .catch((err) => {
                console.error("Error al procesar usuarios:", err);
                openSnack(t("userController.snackbar.processError"), "error");
            })
            .finally(() => setIsProcessing(false));
    };

    const handleProcessRoleAssociations = async () => {
        // Filtrar grupos válidos (groupId definido, userIds no vacíos, addUsers definido)
        const groups = (associateRolesUsers ?? []).filter(
            (g) =>
                g.groupId &&
                Array.isArray(g.userIds) &&
                g.userIds.length > 0 &&
                typeof g.addUsers === "boolean"
        ) as ModifyUsersInGroupsModel[];

        if (groups.length === 0) {
            // opcional: snack informativo; tú pediste éxito/fracaso
            return;
        }

        setIsProcessingRoles(true);
        try {
            await userAzureService.modifyUsersInGroups({
                projectId: project.id,
                groups,
            });
            openSnack(t("userController.snackbar.rolesSuccess"), "success");
            onOpenChange(false); // cerrar modal al éxito
        } catch (e) {
            console.error("Error al actualizar roles:", e);
            openSnack(t("userController.snackbar.rolesError"), "error");
        } finally {
            setIsProcessingRoles(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onOpenChange(false)} fullWidth maxWidth="md">
            <DialogTitle sx={{ typography: "h6" }}>{t("userController.title")}</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ px: 3, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("userController.subtitle")}
                    </Typography>

                    <Tabs
                        value={tabIndex}
                        onChange={(_, idx: number) => {
                            let newUserType: UserTab = "create";
                            switch (idx) {
                                case 0:
                                    newUserType = "create";
                                    break;
                                case 1:
                                    newUserType = "invite";
                                    break;
                                case 2:
                                    newUserType = "associate";
                                    break;
                                default:
                                    newUserType = "create";
                                    break;
                            }
                            setUserType(newUserType);
                        }}
                        aria-label={t("userController.a11y.tabsAria")}
                        sx={{ mt: 2 }}
                    >
                        <Tab
                            icon={<PersonAddAlt fontSize="small" />}
                            iconPosition="start"
                            label={t("userController.tab.create")}
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={<MailOutline fontSize="small" />}
                            iconPosition="start"
                            label={t("userController.tab.invite")}
                            {...a11yProps(1)}
                        />
                        <Tab
                            icon={<Security fontSize="small" />}
                            iconPosition="start"
                            label={t("userController.tab.associate")}
                            {...a11yProps(2)}
                        />
                    </Tabs>

                    {tabIndex === 0 && (
                        <CreateUser
                            roles={roles}
                            validDomain={validDomain}
                            onAdd={(payload) =>
                                handleAddUser({
                                    isNewUser: true,
                                    emailOrUsername: payload.username,
                                    name: payload.firstName,
                                    roleId: payload.roleId,
                                    allowAccess: payload.allowAccess,
                                })
                            }
                        />
                    )}

                    {tabIndex === 1 && (
                        <InviteExternUser
                            roles={roles}
                            onAdd={(payload) =>
                                handleAddUser({
                                    isNewUser: false,
                                    emailOrUsername: payload.email,
                                    roleId: payload.roleId,
                                    allowAccess: payload.allowAccess,
                                })
                            }
                        />
                    )}

                    {tabIndex === 2 && (
                        <AssociateUsersRoles
                            roles={roles}
                            associateRolesUsers={associateRolesUsers}
                            setAssociateRolesUsers={setAssociateRolesUsers}
                        />
                    )}

                    {userType !== "associate" && (
                        <Paper variant="outlined" sx={{ mt: 3, mb: 1 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    bgcolor: (t_) => t_.palette.action.hover,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t("userController.pending.title", {
                                        count: pendingUsers.length,
                                    })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("userController.pending.subtitle")}
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {t("userController.pending.headers.type")}
                                            </TableCell>
                                            <TableCell>
                                                {t("userController.pending.headers.email")}
                                            </TableCell>
                                            <TableCell>
                                                {t("userController.pending.headers.name")}
                                            </TableCell>
                                            <TableCell>
                                                {t("userController.pending.headers.role")}
                                            </TableCell>
                                            <TableCell>
                                                {t("userController.pending.headers.initialPerms")}
                                            </TableCell>
                                            <TableCell align="right" width={60} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pendingUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ py: 2, textAlign: "center" }}
                                                    >
                                                        {t("userController.pending.empty")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            pendingUsers.map((user) => (
                                                <TableRow hover key={user.id}>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                user.isNewUser
                                                                    ? t("userController.tag.create")
                                                                    : t("userController.tag.invite")
                                                            }
                                                            color={
                                                                user.isNewUser
                                                                    ? "primary"
                                                                    : "default"
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            component="span"
                                                            sx={{ fontFamily: "monospace" }}
                                                        >
                                                            {user.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{user.name || "-"}</TableCell>
                                                    <TableCell>
                                                        {
                                                            roles.find(
                                                                (rr) =>
                                                                    rr.principalId === user.roleId
                                                            )?.principalDisplayName
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.allowAccess
                                                            ? t("userController.yes")
                                                            : t("userController.no")}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            aria-label={t(
                                                                "userController.pending.actions.removeAria"
                                                            )}
                                                            size="small"
                                                            onClick={() =>
                                                                handleRemoveUser(user.id)
                                                            }
                                                        >
                                                            <DeleteOutline
                                                                color="error"
                                                                fontSize="small"
                                                            />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            resetAll();
                            onOpenChange(false);
                        }}
                        disabled={isProcessing || isProcessingRoles}
                    >
                        {t("userController.buttons.cancel")}
                    </Button>

                    {userType !== "associate" ? (
                        <Button
                            variant="contained"
                            onClick={handleProcessAll}
                            disabled={pendingUsers.length === 0 || isProcessing}
                            startIcon={isProcessing ? <CircularProgress size={18} /> : undefined}
                            color={
                                !(pendingUsers.length === 0 || isProcessing) ? "primary" : "inherit"
                            }
                        >
                            {isProcessing
                                ? t("userController.buttons.processing")
                                : t("userController.buttons.processAll", {
                                      count: pendingUsers.length,
                                  })}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleProcessRoleAssociations}
                            disabled={isProcessingRoles}
                            startIcon={
                                isProcessingRoles ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <Security fontSize="small" />
                                )
                            }
                        >
                            {t("userController.buttons.updateRoles")}
                        </Button>
                    )}
                </Box>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
